import { useState } from 'react';
import { Box, TextField, Chip, IconButton, Typography } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useCaseFieldData, useCaseFieldDispatch } from '../../hooks/useCaseField';
import type { FieldMode } from '../../hooks/useCaseField';
import type { MedNecCaseView } from '../../../../services/MedNecService/types/medNecTypes';

interface CaseChipListFieldProps {
  mode: FieldMode;
  label?: string;
  placeholder?: string;
  chipColor?: 'default' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
  chipVariant?: 'filled' | 'outlined';
  getItems: (data: MedNecCaseView) => string[];
  getUpdate: (items: string[], data: MedNecCaseView) => Partial<MedNecCaseView>;
}

const CaseChipListField: React.FC<CaseChipListFieldProps> = ({
  mode, label, placeholder, chipColor = 'default', chipVariant = 'outlined',
  getItems, getUpdate,
}) => {
  const data = useCaseFieldData(mode);
  const { dispatchUpdate, readOnly } = useCaseFieldDispatch(mode);
  const items = getItems(data);
  const [input, setInput] = useState('');

  const addItem = () => {
    const trimmed = input.trim();
    if (!trimmed || items.includes(trimmed)) return;
    dispatchUpdate(getUpdate([...items, trimmed], data));
    setInput('');
  };

  const removeItem = (index: number) => {
    dispatchUpdate(getUpdate(items.filter((_, i) => i !== index), data));
  };

  return (
    <Box>
      {label && <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>{label}</Typography>}
      {items.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
          {items.map((item, i) => (
            <Chip key={i} label={item} size="small" color={chipColor} variant={chipVariant}
              onDelete={readOnly ? undefined : () => removeItem(i)} />
          ))}
        </Box>
      )}
      {!readOnly && (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            size="small" placeholder={placeholder ?? `Add ${label?.toLowerCase() ?? 'item'}...`}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addItem(); } }}
            sx={{ flex: 1, maxWidth: 300 }}
          />
          <IconButton size="small" onClick={addItem}><AddIcon fontSize="small" /></IconButton>
        </Box>
      )}
    </Box>
  );
};

export default CaseChipListField;
